import TagManager, { TagManagerArgs } from "react-gtm-module";
import { stripUndefined } from "../js_utils/object_utils";
import { Analytics, EcommerceEvent, ProductProperties } from "./types";

/**
 * GTM (Google Tag Manager) Analytics Integration
 *
 * Context on gtag.js initialization and event propagation:
 *
 * - react-ga4 uses gtag.js behind it to initialize Flexspace ga4TrackingId
 * - react-gtm-module also loads gtag.js to initialize its configured ga4TrackingId
 *   inside GTM dashboard
 *
 * After both initialize, you'll have:
 *
 * window.dataLayer = [
 *   ['js', Date],
 *   ['config', 'G-68BJCMB8NC'],  // From react-ga4
 *   ['config', 'G-J8R1KHNGYN'],  // From GTM (if GTM configures it)
 *   // ... events
 * ]
 *
 * When GA4 react code does the logic and sends event: ReactGA.event('view_item', { ... })
 * for the Flexspace GA4 in ga4.ts, this translates into gtag('event', 'view_item', { ... }).
 *
 * This is sent through all the ga4TrackingIds initialized with gtag.js (including the one
 * loaded by GTM).
 *
 * As we always initialize Flexspace GA4 tracking by default on all WL,
 * GA4 ecommerce events sent through GTM will work out of the box.
 */

export class GTMSourceProvider {
  initialized: boolean = false;

  initialize(args: TagManagerArgs) {
    // There can only be 1 GTM tag
    if (!this.initialized) {
      TagManager.initialize(args);
      this.initialized = true;
    }

    return gtmAnalytics;
  }
}

export interface GTMClient extends GTMClientArgs {
  provider: "gtm";
}

export interface GTMClientArgs extends TagManagerArgs {
  sendPageView?: boolean;
}

export function makeGTMClient(args: GTMClientArgs): GTMClient {
  return {
    ...args,
    provider: "gtm",
    sendPageView: args.sendPageView,
  };
}

const gtmAnalytics: Analytics = {
  group: () => {},
  register: () => {},
  identify: () => {},
  screen: (name) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "pageview",
        page: {
          ...(name && { title: name }),
          path: window.location.pathname,
          href: window.location.href,
        },
      },
    });
  },
  event: (eventName, props) => {
    TagManager.dataLayer({
      dataLayer: {
        event: eventName,
        ...props,
      },
    });
  },
  ecommerceEvent: (event) => {
    sendGTMEcommerceEvent(event);
  },
};

function sendGTMEcommerceEvent(event: EcommerceEvent) {
  switch (event.event) {
    case "Product Viewed":
      TagManager.dataLayer({
        dataLayer: {
          ecommerce: null,
        },
      });
      TagManager.dataLayer({
        dataLayer: {
          event: "eec.detail",
          ecommerce: {
            detail: {
              products: event.products.map(toProduct),
            },
          },
        },
      });
      break;

    case "Checkout Started":
      TagManager.dataLayer({
        dataLayer: {
          ecommerce: null,
        },
      });
      TagManager.dataLayer({
        dataLayer: {
          event: "eec.checkout",
          ecommerce: {
            checkout: {
              actionField: { step: 1 },
              products: event.products.map(toProduct),
            },
          },
        },
      });

      break;

    case "Promotion Viewed":
      TagManager.dataLayer({
        dataLayer: {
          ecommerce: null,
        },
      });
      TagManager.dataLayer({
        dataLayer: {
          event: "view_promotion",
          ecommerce: {
            view_promotion: {
              actionField: { promotion_id: event.id },
              products: event.items.map(toProduct),
            },
          },
        },
      });
      break;

    case "Promotion Clicked":
      TagManager.dataLayer({
        dataLayer: {
          ecommerce: null,
        },
      });
      TagManager.dataLayer({
        dataLayer: {
          event: "select_promotion",
          ecommerce: {
            select_promotion: {
              actionField: { promotion_id: event.id },
              products: event.items.map(toProduct),
            },
          },
        },
      });
      break;

    case "Order Completed":
      TagManager.dataLayer({
        dataLayer: {
          ecommerce: null,
        },
      });
      TagManager.dataLayer({
        dataLayer: {
          event: "eec.purchase",
          ecommerce: {
            currencyCode: event.currency,
            purchase: {
              actionField: stripUndefined({
                id: event.id,
                affiliation: event.affiliation,
                revenue: event.revenue
                  ? String(event.revenue.toFixed(2))
                  : undefined,
                tax: event.tax ? String(event.tax.toFixed(2)) : undefined,
                shipping: event.shipping
                  ? String(event.shipping.toFixed(2))
                  : undefined,
                coupon: event.coupon,
              }),
              products: event.products.map(toProduct),
            },
          },
        },
      });
      break;
    default:
      console.error(`Analytics: event ${event.event} not supported yet.`);
      return ["", {}];
  }
}

function toProduct(p: ProductProperties) {
  return stripUndefined({
    id: p.id,
    sku: p.sku,
    name: p.name,
    category: p.category,
    brand: p.brand,
    variant: p.variant,
    list: p.list,
    position: p.position,
    price: p.price ? String(p.price.toFixed(2)) : undefined,
    quantity: p.quantity,
    coupon: p.coupon,
  });
}
